import React, { useState, useEffect } from 'react';
import { IoClose } from "react-icons/io5";
import { FaMapMarkerAlt, FaPlus, FaRegBuilding, FaRegAddressCard, FaTrashAlt, FaCheckCircle, FaChevronRight } from "react-icons/fa";
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import service from '../common/HttpService';
import useTranslate from '../hooks/useTranslate';

const SavedAddressDrawer = ({ onClose, onSelect }) => {
    const user = useSelector(state => state?.user?.user);
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [editId, setEditId] = useState(null);

    const t = useTranslate({
        savedAddresses: "Saved Addresses",
        deliveryLocations: "Your Delivery Locations",
        addNewAddress: "Add New Address",
        noAddresses: "No saved addresses found",
        active: "Active",
        edit: "Edit",
        delete: "Delete",
        updateAddress: "Update Address",
        cancel: "Cancel",
        fullName: "Full Name",
        phonePlaceholder: "Phone Number (10 digits)",
        fullAddress: "Full Address",
        city: "City",
        pincodePlaceholder: "Pincode (6 digits)",
        state: "State",
        saving: "Saving...",
        saveAddress: "Save Address",
        closeAddressBook: "Close Address Book",
        home: "Home",
        work: "Work",
        addressUpdated: "Address updated",
        addressAdded: "Address added successfully",
        failedSave: "Failed to save address",
        errorSaving: "An error occurred while saving",
        deleteConfirm: "Are you sure you want to delete this address?",
        addressDeleted: "Address deleted",
        failedDelete: "Failed to delete address",
        errorDeleting: "Error deleting address",
        addressSelected: "Address selected",
        errorSelecting: "Error selecting address",
        loginSave: "Please login to save address",
        pincodeError: "Please enter a valid 6-digit Pincode",
        phoneError: "Please enter a valid 10-digit phone number"
    });

    // Form state
    const [formData, setFormData] = useState({
        name: '', type: t.home, phone: '', address: '', city: '', state: '', pincode: ''
    });

    const fetchAddresses = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const response = await service.get(SummaryApi.getAllAddress.url);
            if (response.success === "true" || response.success === true) {
                const data = response.extraData;
                const addrList = data?.address && Array.isArray(data.address)
                    ? data.address
                    : (Array.isArray(data) ? data : (data ? [data] : []));
                setAddresses(addrList);
            } else {
                setAddresses([]);
            }
        } catch (error) {
            console.error("Error fetching addresses:", error);
            setAddresses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?._id || user?.id) {
            fetchAddresses();
        }
    }, [user]);

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userId = user?._id || user?.id;

            if (!userId) {
                toast.error(t.loginSave);
                setLoading(false);
                return;
            }

            const payload = {
                user_id: userId,
                name: formData.name,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pin: formData.pincode,
                mobile: formData.phone,
                type: formData.type
            };

            // Mobile validation: exactly 10 digits
            if (!/^\d{10}$/.test(payload.mobile)) {
                toast.error(t.phoneError);
                setLoading(false);
                return;
            }

            // Pincode validation: exactly 6 digits
            if (!/^\d{6}$/.test(payload.pin)) {
                toast.error(t.pincodeError);
                setLoading(false);
                return;
            }

            let response;
            if (editId) {
                payload.address_id = editId;
                response = await service.post(SummaryApi.updateShippingAddress.url, payload);
            } else {
                response = await service.post(SummaryApi.addShippingAddress.url, payload);
            }

            if (response.success === "true" || response.success === true) {
                toast.success(response.message || (editId ? t.addressUpdated : t.addressAdded));
                setIsAdding(false);
                setEditId(null);
                setFormData({ name: '', type: t.home, phone: '', address: '', city: '', state: '', pincode: '' });
                fetchAddresses();
            } else {
                toast.error(response.message || t.failedSave);
            }
        } catch (error) {
            console.error("Save address error:", error);
            toast.error(error?.data?.message || t.errorSaving);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (e, addr) => {
        e.stopPropagation();
        setEditId(addr.address_id || addr.id || addr._id);
        setFormData({
            name: addr.name,
            type: addr.type || t.home,
            phone: addr.mobile,
            address: addr.address,
            city: addr.city,
            state: addr.state,
            pincode: addr.pin
        });
        setIsAdding(true);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm(t.deleteConfirm)) return;

        try {
            const userId = user?._id || user?.id;
            const response = await service.post(SummaryApi.deleteShippingAddress.url, {
                address_id: id,
                user_id: userId
            });

            if (response.success === "true" || response.success === true) {
                toast.success(response.message || t.addressDeleted);
                fetchAddresses();
            } else {
                toast.error(response.message || t.failedDelete);
            }
        } catch (error) {
            console.error("Delete address error:", error);
            toast.error(t.errorDeleting);
        }
    };

    const handleSelectAddress = async (addr) => {
        const id = addr.address_id || addr.id || addr._id;
        try {
            const userId = user?._id || user?.id;
            // Set as default on backend
            const response = await service.post(SummaryApi.setDefaultShippingAddress.url, {
                address_id: id,
                user_id: userId
            });
            if (response.success === "true" || response.success === true) {
                toast.success(t.addressSelected);
                fetchAddresses(); // Refresh UI to show new default
                if (onSelect) {
                    onSelect(id);
                } else {
                    onClose();
                }
            }
        } catch (error) {
            toast.error(t.errorSelecting);
            // Still try to select locally if backend fails
            if (onSelect) onSelect(id);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/50 backdrop-blur-sm">
            <div className="flex-1" onClick={onClose}></div>

            <div className="w-full max-w-md h-full bg-[#f8faf9] shadow-2xl flex flex-col">

                {/* PREMIUM THEME HEADER */}
                <div className="relative bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] px-6 py-8 overflow-hidden text-white">

                    {/* Glow Effects */}
                    <div className="absolute -top-12 -left-12 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-[#00b259]/10 rounded-full blur-3xl"></div>

                    <button
                        onClick={onClose}
                        className="absolute right-5 top-5 bg-white/10 hover:bg-white/20 p-2 rounded-full backdrop-blur-sm transition"
                    >
                        <IoClose size={20} className="text-white" />
                    </button>

                    <div className="relative z-10 flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#00b259] to-[#008f47] rounded-2xl flex items-center justify-center shadow-lg border border-white/20">
                            <FaMapMarkerAlt size={22} className="text-white" />
                        </div>

                        <div>
                            <h2 className="text-xl text-white uppercase tracking-tight">
                                {t.savedAddresses}
                            </h2>
                            <p className="text-xs text-[#00ff9d] font-bold uppercase tracking-[2px] mt-1">
                                {t.deliveryLocations}
                            </p>
                        </div>
                    </div>
                </div>

                {/* BODY */}
                <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5">

                    {!isAdding ? (
                        <>
                            {/* ADD BUTTON */}
                            <button
                                onClick={() => setIsAdding(true)}
                                className="w-full bg-white border-2 border-dashed border-gray-200 rounded-2xl py-4 flex items-center justify-center gap-2 text-sm font-semibold text-gray-500 hover:border-[#00b259] hover:text-[#00b259] transition shadow-sm"
                            >
                                <FaPlus size={14} />
                                {t.addNewAddress}
                            </button>

                            {/* EMPTY */}
                            {!loading && addresses.length === 0 && (
                                <div className="text-center py-16 text-gray-400 text-sm">
                                    {t.noAddresses}
                                </div>
                            )}

                            {/* LOADING */}
                            {loading && (
                                <div className="flex justify-center py-10">
                                    <div className="w-6 h-6 border-2 border-gray-200 border-t-[#00b259] rounded-full animate-spin"></div>
                                </div>
                            )}

                            {/* ADDRESS LIST */}
                            <div className="space-y-3">
                                {addresses.map(addr => {
                                    const addrId = addr.address_id || addr.id || addr._id;
                                    const isSelected =
                                        addr.is_default === "true" ||
                                        addr.default_address === "1" ||
                                        addr.default_address === true;

                                    return (
                                        <div
                                            key={addrId}
                                            onClick={() => handleSelectAddress(addr)}
                                            className={`bg-white border rounded-xl p-3 cursor-pointer transition-all shadow-sm relative overflow-hidden flex flex-col gap-2 ${isSelected
                                                ? "border-[#00b259] bg-[#f4fbf7]"
                                                : "border-gray-100 hover:border-[#00b259]/30"
                                                }`}
                                        >
                                            <div className="flex justify-between items-start gap-3">
                                                {/* ICON & TYPE */}
                                                <div
                                                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isSelected
                                                        ? "bg-[#00b259] text-white"
                                                        : "bg-gray-50 text-gray-400"
                                                        }`}
                                                >
                                                    {addr.type === "Work"
                                                        ? <FaRegBuilding size={14} />
                                                        : <FaRegAddressCard size={14} />}
                                                </div>

                                                {/* DETAILS AREA */}
                                                <div className="flex-1 min-w-0 pr-12">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-bold text-xs text-[#143d2c] truncate uppercase tracking-tight">
                                                            {addr.name}
                                                        </h4>
                                                        <span className="text-[8px] bg-[#00b259]/10 text-[#00b259] px-1.5 py-0.5 rounded font-black uppercase tracking-wider">
                                                            {addr.type || t.home}
                                                        </span>
                                                    </div>

                                                    <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1 italic">
                                                        {addr.address}
                                                    </p>

                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] font-black text-[#143d2c]/60 uppercase tracking-tighter">
                                                            {addr.city} • {addr.pin}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* DEFAULT BADGE - Compact */}
                                                {isSelected && (
                                                    <div className="absolute top-0 right-0">
                                                        <div className="bg-[#00b259] text-white text-[7px] font-black px-2 py-0.5 rounded-bl-lg shadow-sm uppercase tracking-widest flex items-center gap-1">
                                                            <FaCheckCircle size={8} /> {t.active}
                                                        </div>
                                                    </div>
                                                )}

                                            </div>

                                            {/* BOTTOM ACTIONS BAR - Very Compact */}
                                            <div className="flex justify-between items-center pt-2 mt-1 border-t border-gray-50">
                                                <div className="flex gap-4">
                                                    <button
                                                        onClick={(e) => handleEdit(e, addr)}
                                                        className="text-[10px] font-black text-slate-400 hover:text-[#00b259] uppercase transition-colors"
                                                    >
                                                        {t.edit}
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDelete(e, addrId)}
                                                        className="text-[10px] font-black text-slate-300 hover:text-red-500 uppercase transition-colors flex items-center gap-1"
                                                    >
                                                        <FaTrashAlt size={8} /> {t.delete}
                                                    </button>
                                                </div>

                                                <p className="text-[10px] font-bold text-gray-400">
                                                    📞 {addr.mobile}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        /* ADD FORM */
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-semibold text-[#143d2c]">
                                    {editId ? t.updateAddress : t.addNewAddress}
                                </h3>
                                <button
                                    onClick={() => { setIsAdding(false); setEditId(null); setFormData({ name: '', type: t.home, phone: '', address: '', city: '', state: '', pincode: '' }); }}
                                    className="text-sm text-gray-400 hover:text-red-500"
                                >
                                    {t.cancel}
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="space-y-4">

                                <input
                                    required
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={t.fullName}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none"
                                />

                                <input
                                    required
                                    type="text"
                                    maxLength={10}
                                    value={formData.phone}
                                    onChange={e => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setFormData({ ...formData, phone: val });
                                    }}
                                    placeholder={t.phonePlaceholder}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none"
                                />

                                <textarea
                                    required
                                    value={formData.address}
                                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    placeholder={t.fullAddress}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none min-h-[90px]"
                                />

                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        required
                                        value={formData.city}
                                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                                        placeholder={t.city}
                                        className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none"
                                    />

                                    <input
                                        required
                                        type="text"
                                        maxLength={6}
                                        value={formData.pincode}
                                        onChange={e => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setFormData({ ...formData, pincode: val });
                                        }}
                                        placeholder={t.pincodePlaceholder}
                                        className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none"
                                    />
                                </div>

                                <input
                                    required
                                    value={formData.state}
                                    onChange={e => setFormData({ ...formData, state: e.target.value })}
                                    placeholder={t.state}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#00b259] outline-none"
                                />

                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-[#143d2c] to-[#00b259] text-white py-3 rounded-xl text-sm font-semibold hover:opacity-95 transition"
                                >
                                    {loading ? t.saving : (editId ? t.updateAddress : t.saveAddress)}
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {/* FOOTER CLOSE BUTTON */}
                <div className="p-5 bg-white border-t border-gray-50 mt-auto">
                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] hover:brightness-110 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all uppercase tracking-[3px] text-[11px] shadow-xl shadow-green-900/20 active:scale-[0.98]"
                    >
                        {t.closeAddressBook}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SavedAddressDrawer;
